import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/shared/api/apiClient';
import { handleRequest } from '@/shared/api/apiWrapper';
import type { CryptoDepositAddressResponse } from '@/shared/types';
import { CACHE_TIME_10_MINUTES, CACHE_TIME_5_MINUTES } from '@/shared/config';

const getCryptoDepositAddress = async (
  networkName: string,
  assetCode: string
): Promise<CryptoDepositAddressResponse> => {
  const request = () =>
    apiClient.get(
      `/operations/crypto/${networkName}/deposit?assetCode=${assetCode}`
    );
  const response = await handleRequest(request);

  return response.data;
};

interface UseCryptoDepositAddressOptions {
  networkName: string;
  assetCode: string;
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
}

export function useCryptoDepositAddress({
  networkName,
  assetCode,
  enabled = true,
  staleTime = CACHE_TIME_5_MINUTES,
  gcTime = CACHE_TIME_10_MINUTES,
}: UseCryptoDepositAddressOptions) {
  const queryKey = ['cryptoDepositAddress', networkName, assetCode];

  const query = useQuery<CryptoDepositAddressResponse, Error>({
    queryKey,
    queryFn: async () => {
      return await getCryptoDepositAddress(networkName, assetCode);
    },
    enabled: enabled && !!networkName && !!assetCode,
    staleTime,
    gcTime,
    retry: 0,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    status: query.status,
    refetch: query.refetch,
    query,
  };
}
